/**
 * Update-Pruefung der Android-Huelle (Paket D, docs/android-app-plan.md
 * Abschnitt 4 „App-Updates").
 *
 * Die WEB-Inhalte aktualisieren sich in der App wie im Browser (Bauart A,
 * Versions-Gate). Hier geht es nur um die HUELLE: liegt im Eimer eine APK mit
 * hoeherem `versionCode`, zeigt die App eine wegklickbare Leiste; liegt der
 * eigene Stand unter `minVersionCode`, ein Sperrbild. Der Knopf
 * aktualisiert IN der App (Vertrag E21, `apkUpdate.ts`: Download, sha256,
 * PackageInstaller-Session); kennt die installierte Huelle das Plugin
 * `ApkUpdate` nicht oder scheitert es, fuehrt „Im Browser laden" wie bisher
 * ueber den signierten APK-Link im System-Browser. Android installiert in
 * beiden Faellen ueber die alte App (gleicher Schluessel, hoeherer
 * versionCode → Anmeldung und Daten bleiben).
 *
 * Geprueft wird die Release-Info der EIGENEN Variante (`appVariante()`,
 * Vertrag E13): ein Handy bekommt nie die Tablet-APK angeboten (andere appId,
 * sie installierte sich als zweite App daneben).
 *
 * Nur in der App aktiv (`istNativeApp()` und bekannte Variante); im Browser
 * ist der Hook stumm und der Status immer 'aktuell'.
 */

import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { appVariante, istNativeApp, nativeBuild } from './nativePlatform';
import { externOeffnen } from './externOeffnen';
import { apkDownloadLink, appDownloadSeitenUrl, ladeAppRelease, type AppRelease } from './appRelease';
import {
    apkInstallErlaubt,
    apkUpdateVerfuegbar,
    bereiteApkUpdateVor,
    brichApkUpdateAb,
    installiereApkUpdate,
    type ApkUpdateErgebnis,
    type ApkUpdatePhase,
    type ApkUpdateStatus,
    type ApkVorbereitung,
} from './apkUpdate';
import { hasPendingBauphaseFlushers, runBauphaseFlushers } from './bauphaseFlush';

export type UpdateStatus = 'aktuell' | 'hinweis' | 'pflicht';

/** Was `nativeBuild()` liefert — der installierte Stand der Huelle. */
export interface InstallierteVersion {
    versionName: string;
    versionCode: number;
}

/**
 * Rein: installierter versionCode + Release-Info → Status.
 *
 * Unbekannt (Web, alte Huelle ohne App-Plugin, kaputter Wert) oder keine
 * Release-Info → 'aktuell'. Im Zweifel nie sperren: ein Sperrbild ohne Grund
 * waere ein Totalausfall der App.
 */
export function updateStatus(installiert: number | null, release: AppRelease | null): UpdateStatus {
    if (!release) return 'aktuell';
    if (typeof installiert !== 'number' || !Number.isInteger(installiert) || installiert < 1) return 'aktuell';
    if (installiert < release.minVersionCode) return 'pflicht';
    if (release.versionCode > installiert) return 'hinweis';
    return 'aktuell';
}

export interface UpdateBefund {
    status: UpdateStatus;
    release: AppRelease | null;
    installiert: InstallierteVersion | null;
}

export const LEERER_BEFUND: UpdateBefund = { status: 'aktuell', release: null, installiert: null };

export interface UpdatePrueferOptionen {
    ladeRelease: () => Promise<AppRelease | null>;
    ladeInstalliert: () => Promise<InstallierteVersion | null>;
    /** Mindestabstand zwischen zwei echten Pruefungen. Standard 5 Minuten (wie versionGate). */
    minIntervalMs?: number;
    now?: () => number;
}

/**
 * Gedrosselte Pruefung — Muster `createVersionGate`: innerhalb von
 * `minIntervalMs` wird nicht neu geladen, sondern der letzte Befund
 * wiederholt; wer waehrend einer laufenden Pruefung fragt, bekommt deren
 * Ergebnis statt einer zweiten Anfrage.
 *
 * Liefert eine Pruefung KEINE Release-Info (Netz weg, Eimer nicht lesbar),
 * bleibt die zuletzt gesehene stehen: ein Funkloch auf der Baustelle soll ein
 * berechtigtes Sperrbild nicht aufheben und die Leiste nicht flackern lassen.
 */
export const createUpdatePruefer = (opts: UpdatePrueferOptionen) => {
    const { ladeRelease, ladeInstalliert, minIntervalMs = 5 * 60 * 1000, now = Date.now } = opts;

    let letzterCheck = -Infinity;
    let befund: UpdateBefund = LEERER_BEFUND;
    let laufend: Promise<UpdateBefund> | null = null;

    const sicher = async <T>(f: () => Promise<T | null>): Promise<T | null> => {
        try {
            return await f();
        } catch {
            return null;
        }
    };

    const pruefe = async (): Promise<UpdateBefund> => {
        if (laufend) return laufend;
        const t = now();
        if (t - letzterCheck < minIntervalMs) return befund;
        letzterCheck = t;
        laufend = (async () => {
            try {
                const [release, installiert] = await Promise.all([sicher(ladeRelease), sicher(ladeInstalliert)]);
                const r = release ?? befund.release;
                const i = installiert ?? befund.installiert;
                befund = { status: updateStatus(i?.versionCode ?? null, r), release: r, installiert: i };
            } finally {
                laufend = null;
            }
            return befund;
        })();
        return laufend;
    };

    return {
        pruefe,
        /** Der letzte Befund ohne neue Pruefung (Startwert fuer den Hook). */
        letzterBefund: (): UpdateBefund => befund,
    };
};

/** Release-Info der eigenen Variante; ohne bekannte Variante keine Pruefung (null). */
const ladeEigeneRelease = async (): Promise<AppRelease | null> => {
    const variante = appVariante();
    return variante ? ladeAppRelease(variante) : null;
};

// Ein Pruefer fuer die ganze App: Leiste und Download-Seite teilen Drossel
// und Befund — sonst pruefte jede Stelle fuer sich.
let geteilterPruefer: ReturnType<typeof createUpdatePruefer> | null = null;

const pruefer = () => {
    if (!geteilterPruefer) {
        geteilterPruefer = createUpdatePruefer({ ladeRelease: ladeEigeneRelease, ladeInstalliert: nativeBuild });
    }
    return geteilterPruefer;
};

/** Laeuft die Update-Pruefung auf diesem Geraet? Nur in der App mit bekannter Variante. */
const pruefungAktiv = (): boolean => istNativeApp() && appVariante() !== null;

/**
 * Den APK-Download im System-Browser oeffnen. Wirft nie.
 *
 * Ohne Release-Info wird die der eigenen Variante nachgeladen; laesst sich
 * kein signierter Link
 * erzeugen (Netz, Recht), oeffnet der System-Browser die Download-Seite
 * `/#/app` — dort kann sich der Nutzer anmelden und von Hand laden.
 *
 * Im Browser (nicht in der App) wird NICHTS extern geoeffnet: nach einem
 * `await` wuerde ein Popup-Blocker den neuen Tab schlucken. Dort fuehrt der
 * Weg auf die Download-Seite im selben Fenster.
 */
export async function oeffneApkDownload(release: AppRelease | null): Promise<void> {
    try {
        if (!istNativeApp()) {
            window.location.hash = '#/app';
            return;
        }
        const r = release ?? (await ladeEigeneRelease());
        const link = r ? await apkDownloadLink(r) : null;
        externOeffnen(link ?? appDownloadSeitenUrl(window.location.origin, window.location.pathname));
    } catch {
        // nie werfen — der Knopf bleibt, der Nutzer kann es erneut versuchen
    }
}

// ─── Update in der App (Vertrag E21, Review R4, Codex R4) ────────────────────

/**
 * Anzeige-Stufe des In-App-Updates. `ruhe` = nichts laeuft; `bereit` = geladen
 * und geprueft, wartet auf „Jetzt installieren"; `sichern` = die Web-Seite
 * sichert die Eingaben, bevor die Installation die App beendet.
 */
export type InstallPhase = 'ruhe' | 'sichern' | ApkUpdatePhase;

export interface InstallZustand {
    phase: InstallPhase;
    /** Nur bei `laden` mit bekannter Groesse. */
    prozent: number | null;
    /** Nur bei `fehler`. */
    meldung: string | null;
    /**
     * Nur bei `fehler`: das Sichern war nicht bestaetigt, die gepruefte Datei
     * liegt bereit — „Erneut versuchen" sichert erneut und installiert dann
     * (kein neuer Download, kein Browser-Rueckfall).
     */
    nurInstallation?: boolean;
}

export const INSTALL_RUHE: InstallZustand = { phase: 'ruhe', prozent: null, meldung: null };

/** Text, wenn die Sicherung vor der Installation nicht bestaetigt wurde (Entscheid Codex R4 P1). */
export const SICHERN_NICHT_BESTAETIGT = 'Sichern nicht bestätigt — bitte erneut versuchen (oder später installieren).';

/** In diesen Stufen startet ein weiterer Tipp auf „Aktualisieren" nichts Neues. */
const LAUFENDE_PHASEN: readonly InstallPhase[] = ['laden', 'pruefen', 'sichern', 'installieren', 'bestaetigen'];

/** Kleiner Speicher fuer die offene Erlaubnis-Anfrage (localStorage, im Test ein Objekt). */
export interface AnfrageMerker {
    lese: () => string | null;
    schreibe: (wert: string | null) => void;
}

export interface InAppUpdaterOptionen {
    /** Kann die Huelle sich selbst aktualisieren (`apkUpdateVerfuegbar`)? */
    verfuegbar: () => boolean;
    /** Phase 1: laden und pruefen (`bereiteApkUpdateVor`), abbrechbar ueber `signal`. */
    bereiteVor: (
        release: AppRelease,
        onStatus: (status: ApkUpdateStatus) => void,
        opts: { signal: AbortSignal },
    ) => Promise<ApkVorbereitung>;
    /** Phase 2: installieren (`installiereApkUpdate`) — beendet die App. */
    installiere: (onStatus: (status: ApkUpdateStatus) => void) => Promise<ApkUpdateErgebnis>;
    /** Laufenden Download im Plugin abbrechen (`brichApkUpdateAb`). */
    brichAb: () => Promise<void>;
    /** Eingaben sichern und BESTAETIGEN (`sichereEingabenVorUpdate`); false = nicht bestaetigt. */
    sichern: () => Promise<boolean>;
    /** Seite „verlassen": `pagehide` (`seiteVerlassenFuerUpdate`) — unmittelbar vor der Installation. */
    verlassen: () => void;
    /** Gegenstueck, wenn die Installation doch nicht kam (`nachUpdateWeiterarbeiten`). */
    weiterarbeiten: () => void;
    /** „Apps aus dieser Quelle zulassen" erteilt? */
    erlaubt: () => Promise<boolean>;
    /** Release-Info der eigenen Variante, falls beim Tippen noch keine bekannt ist. */
    ladeRelease: () => Promise<AppRelease | null>;
    /** Bisheriger Weg: APK im System-Browser (`oeffneApkDownload`). */
    imBrowser: (release: AppRelease | null) => Promise<void>;
    merker?: AnfrageMerker | null;
    now?: () => number;
    /** Wie lange eine Erlaubnis-Anfrage einen Neustart der App ueberdauert. Standard 15 Minuten. */
    anfrageGueltigMs?: number;
}

/**
 * Ablaufsteuerung des In-App-Updates — EIN Zustand fuer die ganze App, damit
 * Leiste, Sperrbild und Seite „App-Info" dasselbe zeigen.
 *
 *  - `starte` (Phase 1): ohne Plugin (Web, alte Huelle) → bisheriger
 *    Browser-Weg; sonst laden und pruefen → `bereit`. `abbrechen` bricht
 *    Laden/Pruefen ab — auch waehrend der Download-Link noch entsteht
 *    (Abbruchsignal bis in `bereiteVor`).
 *  - `jetztInstallieren` (Phase 2): aus `bereit`, aus einem nicht bestaetigten
 *    Sichern (`fehler` mit `nurInstallation`) oder — „Dialog erneut oeffnen" —
 *    aus `bestaetigen`. Ablauf: `sichern` (bestaetigt, Frist 5 s) → NUR bei
 *    Erfolg `verlassen` (`pagehide`) → sofort `installiere`. Nicht bestaetigt
 *    → `fehler` „Sichern nicht bestaetigt …", KEIN `pagehide`, KEINE
 *    Installation. Ein abgeloester Lauf sendet nichts mehr.
 *  - Wurde `pagehide` gesendet und die Installation kommt nicht (Abbruch,
 *    Fehler, Erlaubnis, Browser-Rueckfall, Zuruecksetzen), laeuft
 *    `weiterarbeiten` GENAU EINMAL — gebunden an den Uebergang nach
 *    `fehler`/`erlaubnis_noetig`/`ruhe`.
 *  - `erlaubnis_noetig`: die Einstellungsseite ist offen. Kommt der Nutzer
 *    zurueck (`beiRueckkehr`) und hat erlaubt, geht es mit Phase 1 weiter
 *    (eine schon gepruefte Datei nimmt das Plugin wieder). Android 11 beendet
 *    die App beim Erteilen — `merker` + `nachNeustart` setzen dann fort.
 *  - `fehler`: `imBrowserLaden` ist der Rueckfall.
 *
 * Jeder Start bekommt eine laufende Nummer; Meldungen eines abgeloesten
 * Laufs aendern nichts mehr.
 */
export const createInAppUpdater = (opts: InAppUpdaterOptionen) => {
    const {
        verfuegbar, bereiteVor, installiere, brichAb, sichern, verlassen, weiterarbeiten, erlaubt, ladeRelease, imBrowser,
        merker = null, now = Date.now, anfrageGueltigMs = 15 * 60 * 1000,
    } = opts;

    let zustand: InstallZustand = INSTALL_RUHE;
    const abos = new Set<() => void>();
    let lauf = 0;
    let abbruch: AbortController | null = null;
    let letzteRelease: AppRelease | null = null;
    let erlaubnisPruefungLaeuft = false;
    /** `pagehide` gesendet, ohne dass die App beendet wurde bzw. `pageshow` kam. */
    let seiteVerlassen = false;

    /** Seite verlassen — hoechstens einmal, bis `weiterarbeiten` sie zurueckholt. */
    const verlasseSeite = (): void => {
        if (seiteVerlassen) return;
        seiteVerlassen = true;
        try {
            verlassen();
        } catch {
            // die Installation geht vor
        }
    };
    /** Genau einmal nach einem `pagehide`, wenn die Installation nicht kam. */
    const nimmSeiteWiederAuf = (): void => {
        if (!seiteVerlassen) return;
        seiteVerlassen = false;
        try {
            weiterarbeiten();
        } catch {
            // die Anzeige geht vor
        }
    };

    const setze = (neu: InstallZustand): void => {
        if (neu.phase === 'fehler' || neu.phase === 'erlaubnis_noetig' || neu.phase === 'ruhe') nimmSeiteWiederAuf();
        if (neu.phase === zustand.phase && neu.prozent === zustand.prozent && neu.meldung === zustand.meldung
            && (neu.nurInstallation ?? false) === (zustand.nurInstallation ?? false)) return;
        zustand = neu;
        for (const abo of Array.from(abos)) {
            try {
                abo();
            } catch {
                // eine kaputte Anzeige darf den Ablauf nicht stoppen
            }
        }
    };
    const fehler = (meldung: string): InstallZustand => ({ phase: 'fehler', prozent: null, meldung });
    const stufe = (phase: InstallPhase): InstallZustand => ({ phase, prozent: null, meldung: null });
    const laeuft = (): boolean => LAUFENDE_PHASEN.includes(zustand.phase);

    /** Den laufenden Lauf abloesen: Nummer weiter, Abbruchsignal setzen. */
    const beendeLauf = (): void => {
        lauf++;
        if (abbruch) abbruch.abort();
        abbruch = null;
    };
    /** Neuer Lauf mit eigener Nummer und eigenem Abbruchsignal. */
    const neuerLauf = () => {
        beendeLauf();
        const meinLauf = lauf;
        const steuerung = new AbortController();
        abbruch = steuerung;
        return { aktuell: (): boolean => meinLauf === lauf, signal: steuerung.signal };
    };

    const anfrageMerken = (r: AppRelease): void => {
        try {
            merker?.schreibe(JSON.stringify({ versionCode: r.versionCode, zeit: now() }));
        } catch {
            // ohne Speicher gilt nur der Weg ueber `beiRueckkehr`
        }
    };
    const anfrageLoeschen = (): void => {
        try {
            merker?.schreibe(null);
        } catch {
            // egal
        }
    };
    const anfrageLesen = (): { versionCode: number; zeit: number } | null => {
        try {
            const roh = merker?.lese();
            if (!roh) return null;
            const wert: unknown = JSON.parse(roh);
            if (!wert || typeof wert !== 'object') return null;
            const { versionCode, zeit } = wert as Record<string, unknown>;
            if (typeof versionCode !== 'number' || typeof zeit !== 'number') return null;
            return { versionCode, zeit };
        } catch {
            return null;
        }
    };

    const ausStatus = (s: ApkUpdateStatus): InstallZustand => ({
        phase: s.phase,
        prozent: s.phase === 'laden' && typeof s.prozent === 'number' ? s.prozent : null,
        meldung: s.phase === 'fehler' ? (s.meldung ?? 'Update fehlgeschlagen.') : null,
    });

    /** Stufen-Rueckruf fuer einen Lauf; merkt die letzte Fehlermeldung. */
    const rueckruf = (aktuell: () => boolean) => {
        const r = {
            letzteMeldung: null as string | null,
            onStatus: (s: ApkUpdateStatus): void => {
                if (!aktuell()) return;
                if (s.phase === 'fehler' && s.meldung) r.letzteMeldung = s.meldung;
                setze(ausStatus(s));
            },
        };
        return r;
    };
    const fehlerText = (letzte: string | null): string =>
        letzte ?? (zustand.phase === 'fehler' && zustand.meldung ? zustand.meldung : 'Update fehlgeschlagen.');

    /** Phase 1: laden und pruefen. */
    const starte = async (release: AppRelease | null): Promise<void> => {
        if (laeuft() || zustand.phase === 'bereit') return;
        if (!verfuegbar()) {
            setze(INSTALL_RUHE);
            await imBrowser(release ?? letzteRelease);
            return;
        }
        const { aktuell, signal } = neuerLauf();
        anfrageLoeschen();
        setze(stufe('laden'));
        try {
            const r = release ?? letzteRelease ?? (await ladeRelease());
            if (!aktuell()) return;
            if (!r) {
                setze(fehler('Die Angaben zur neuen Version sind gerade nicht erreichbar. Bitte später erneut versuchen.'));
                return;
            }
            letzteRelease = r;
            const rr = rueckruf(aktuell);
            const ergebnis = await bereiteVor(r, rr.onStatus, { signal });
            if (!aktuell() || ergebnis === 'abgebrochen') return;
            if (ergebnis === 'bereit') {
                setze(stufe('bereit'));
            } else if (ergebnis === 'erlaubnis_noetig') {
                anfrageMerken(r);
                setze(stufe('erlaubnis_noetig'));
            } else {
                setze(fehler(fehlerText(rr.letzteMeldung)));
            }
        } catch (e) {
            if (aktuell()) setze(fehler(e instanceof Error && e.message ? e.message : 'Update fehlgeschlagen.'));
        }
    };

    /**
     * Phase 2: Eingaben sichern (bestaetigt), Seite verlassen, installieren.
     * Aus `bereit`, aus `fehler` mit `nurInstallation` (Sichern nicht
     * bestaetigt) und mit `erneut` aus `bestaetigen` (haengender
     * Android-Dialog — das Plugin verwirft die alte Sitzung).
     */
    const jetztInstallieren = async (optionen?: { erneut?: boolean }): Promise<void> => {
        const erlaubtJetzt = zustand.phase === 'bereit'
            || (zustand.phase === 'fehler' && zustand.nurInstallation === true)
            || (optionen?.erneut === true && zustand.phase === 'bestaetigen');
        if (!erlaubtJetzt || !verfuegbar()) return;
        const { aktuell } = neuerLauf();
        setze(stufe('sichern'));
        let bestaetigt = false;
        try {
            bestaetigt = await sichern();
        } catch {
            bestaetigt = false;
        }
        // Abgeloest waehrend des Sicherns: kein pagehide, keine Installation.
        if (!aktuell()) return;
        if (!bestaetigt) {
            setze({ phase: 'fehler', prozent: null, meldung: SICHERN_NICHT_BESTAETIGT, nurInstallation: true });
            return;
        }
        // Erst jetzt, gesichert und aktuell: Seite verlassen, unmittelbar danach freigeben.
        verlasseSeite();
        setze(stufe('installieren'));
        try {
            const rr = rueckruf(aktuell);
            const ergebnis = await installiere(rr.onStatus);
            if (!aktuell()) return;
            if (ergebnis === 'bestaetigen') {
                // Kam die Absage des Dialogs schon vorher an, bleibt sie stehen.
                if (zustand.phase !== 'fehler' && zustand.phase !== 'fertig') setze(stufe('bestaetigen'));
            } else if (ergebnis === 'fertig') {
                setze(stufe('fertig'));
            } else if (ergebnis === 'erlaubnis_noetig') {
                if (letzteRelease) anfrageMerken(letzteRelease);
                setze(stufe('erlaubnis_noetig'));
            } else {
                setze(fehler(fehlerText(rr.letzteMeldung)));
            }
        } catch (e) {
            if (aktuell()) setze(fehler(e instanceof Error && e.message ? e.message : 'Update fehlgeschlagen.'));
        }
    };

    /** Laden/Pruefen abbrechen: Signal setzen, Anzeige sofort zurueck, das Plugin raeumt auf. */
    const abbrechen = async (): Promise<void> => {
        if (zustand.phase !== 'laden' && zustand.phase !== 'pruefen') return;
        beendeLauf();
        setze(INSTALL_RUHE);
        await brichAb();
    };

    /** Rueckfall: APK im System-Browser. Beendet die Anzeige des In-App-Wegs. */
    const imBrowserLaden = async (release: AppRelease | null): Promise<void> => {
        beendeLauf();
        anfrageLoeschen();
        setze(INSTALL_RUHE);
        await imBrowser(release ?? letzteRelease);
    };

    const erlaubnisDa = async (): Promise<boolean> => {
        erlaubnisPruefungLaeuft = true;
        try {
            return await erlaubt();
        } catch {
            return false;
        } finally {
            erlaubnisPruefungLaeuft = false;
        }
    };

    /** Zurueck in der App (resume/visibilitychange): nach erteilter Erlaubnis weitermachen. */
    const beiRueckkehr = async (): Promise<void> => {
        if (zustand.phase !== 'erlaubnis_noetig' || erlaubnisPruefungLaeuft) return;
        const ok = await erlaubnisDa();
        if (ok && zustand.phase === 'erlaubnis_noetig') await starte(letzteRelease);
    };

    /** Nach einem Neustart der App (Android 11): offene Erlaubnis-Anfrage fortsetzen. */
    const nachNeustart = async (release: AppRelease): Promise<void> => {
        if (zustand.phase !== 'ruhe' || erlaubnisPruefungLaeuft) return;
        const anfrage = anfrageLesen();
        if (!anfrage) return;
        anfrageLoeschen();
        const alter = now() - anfrage.zeit;
        if (anfrage.versionCode !== release.versionCode || alter < 0 || alter > anfrageGueltigMs) return;
        if (!verfuegbar()) return;
        const ok = await erlaubnisDa();
        if (ok && zustand.phase === 'ruhe') await starte(release);
    };

    /** Anzeige zuruecksetzen (Leiste weggeklickt) — nie mitten in einem Lauf. */
    const zuruecksetzen = (): void => {
        if (laeuft()) return;
        beendeLauf();
        anfrageLoeschen();
        setze(INSTALL_RUHE);
    };

    return {
        starte,
        jetztInstallieren,
        abbrechen,
        imBrowserLaden,
        beiRueckkehr,
        nachNeustart,
        zuruecksetzen,
        zustand: (): InstallZustand => zustand,
        abonniere: (abo: () => void): (() => void) => {
            abos.add(abo);
            return () => {
                abos.delete(abo);
            };
        },
    };
};

// ─── Eingaben sichern, bevor die Installation die App beendet ────────────────

const warte = (ms: number): Promise<void> => new Promise((fertig) => { setTimeout(fertig, ms); });

/** `pagehide`/`pageshow` wie beim echten Verlassen; ohne PageTransitionEvent (alte WebView) ein schlichtes Event. */
const seitenEreignis = (typ: 'pagehide' | 'pageshow', persisted: boolean): Event => {
    try {
        if (typeof PageTransitionEvent === 'function') return new PageTransitionEvent(typ, { persisted });
    } catch {
        // unten
    }
    return new Event(typ);
};

/**
 * Eingaben vor der Installation sichern und BESTAETIGEN (Entscheid Codex R4
 * P1). Liefert `true` nur, wenn nichts Ungesichertes mehr gemeldet ist.
 *
 * Grundlage ist die zentrale Registratur src/services/bauphaseFlush.ts: wer
 * Eingaben haelt, die nur im React-State liegen (heute die laufende
 * Freelancer-Begehung, FreelancerInspection.tsx:278), meldet dort eine
 * Sicherung an — und meldet sie nach erfolgreichem Speichern wieder ab
 * (`ungespeichert` → false). Deshalb:
 *
 *  1. Nichts angemeldet (`hasPendingBauphaseFlushers()` false) → nichts zu
 *     sichern → `true`.
 *  2. `runBauphaseFlushers()` abwarten, Frist `fristMs` (5 s). Laeuft sie ab →
 *     `false`.
 *  3. Bestaetigt ist erst, wenn sich alle Halter abgemeldet haben: bis zu
 *     `nachlaufMs` (innerhalb der Frist) auf die leere Registratur warten.
 *     Ein Flusher, der wirft oder dessen Speichern scheitert, bleibt
 *     angemeldet (runBauphaseFlushers schluckt den Fehler, die Anmeldung
 *     bleibt) → `false`.
 *
 * Wirft nie. Das `pagehide` sendet NICHT diese Funktion, sondern der Ablauf
 * erst nach bestaetigtem Sichern (`seiteVerlassenFuerUpdate`).
 */
export async function sichereEingabenVorUpdate(
    opts: { fristMs?: number; nachlaufMs?: number; taktMs?: number } = {},
): Promise<boolean> {
    const { fristMs = 5000, nachlaufMs = 1500, taktMs = 100 } = opts;
    try {
        if (!hasPendingBauphaseFlushers()) return true;
        const frist = Date.now() + fristMs;
        const durch = await Promise.race([
            runBauphaseFlushers().then(() => true, () => true),
            warte(fristMs).then(() => false),
        ]);
        if (!durch) return false;
        const bis = Math.min(frist, Date.now() + nachlaufMs);
        while (hasPendingBauphaseFlushers()) {
            if (Date.now() >= bis) return false;
            await warte(taktMs);
        }
        return true;
    } catch {
        return false;
    }
}

/**
 * Die Seite „verlassen", unmittelbar bevor die Installation die App beendet —
 * ein echtes `pagehide` kommt dann nicht mehr sicher an. Darauf hoeren:
 * QuoteEditor.tsx:1792 (lokale Sicherung + Speichern des Angebots),
 * useDashboardLayout.ts:135 (Dashboard-Anordnung), App.tsx:1302
 * (Projekt-Schliessen erfassen, Projektsperre freigeben — holt der Herzschlag
 * App.tsx:1171/1218 zurueck, falls die App weiterlaeuft). `visibilitychange`
 * wird NICHT ausgeloest: `visibilityState` bliebe 'visible', die Zuhoerer
 * (App.tsx:1359, useDashboardLayout.ts:136) pruefen auf 'hidden'.
 * Berichtseingaben liegen ohnehin schon beim Tippen in der Offline-Warteschlange
 * (App.tsx:2733 `offlineSync.enqueue`, IndexedDB).
 */
export function seiteVerlassenFuerUpdate(): void {
    try {
        window.dispatchEvent(seitenEreignis('pagehide', false));
    } catch (e) {
        console.warn('[appUpdate] pagehide nicht ausloesbar:', e);
    }
}

/**
 * Die Installation kam doch nicht (Abbruch, Fehler): wie bei der Rueckkehr
 * aus dem Back-Forward-Cache `pageshow` mit `persisted` — App.tsx:1330 nimmt
 * die Projekt-Erfassung wieder auf. Die Sperre holt der Herzschlag zurueck.
 */
export function nachUpdateWeiterarbeiten(): void {
    try {
        window.dispatchEvent(seitenEreignis('pageshow', true));
    } catch (e) {
        console.warn('[appUpdate] pageshow nicht ausloesbar:', e);
    }
}

const ANFRAGE_SCHLUESSEL = 'appUpdate.erlaubnisAngefragt';

const lokalerMerker: AnfrageMerker = {
    lese: () => {
        try {
            return window.localStorage.getItem(ANFRAGE_SCHLUESSEL);
        } catch {
            return null;
        }
    },
    schreibe: (wert) => {
        try {
            if (wert === null) window.localStorage.removeItem(ANFRAGE_SCHLUESSEL);
            else window.localStorage.setItem(ANFRAGE_SCHLUESSEL, wert);
        } catch {
            // ohne Speicher gilt nur der Weg ueber `beiRueckkehr`
        }
    },
};

let geteilterUpdater: ReturnType<typeof createInAppUpdater> | null = null;

const inAppUpdater = () => {
    if (!geteilterUpdater) {
        geteilterUpdater = createInAppUpdater({
            verfuegbar: apkUpdateVerfuegbar,
            bereiteVor: bereiteApkUpdateVor,
            installiere: installiereApkUpdate,
            brichAb: brichApkUpdateAb,
            sichern: () => sichereEingabenVorUpdate(),
            verlassen: seiteVerlassenFuerUpdate,
            weiterarbeiten: nachUpdateWeiterarbeiten,
            erlaubt: apkInstallErlaubt,
            ladeRelease: ladeEigeneRelease,
            imBrowser: oeffneApkDownload,
            merker: lokalerMerker,
        });
    }
    return geteilterUpdater;
};

/**
 * Update-Status der App. Nur aktiv, wenn `istNativeApp()` und die Variante
 * bekannt ist (`appVariante()`): prueft beim Einhaengen und beim
 * Zurueckkehren in die App (`visibilitychange`, dazu das Capacitor-Ereignis
 * `resume`), gedrosselt auf hoechstens alle 5 Minuten.
 *
 * `oeffneDownload`: mit Plugin `ApkUpdate` Phase 1 des Updates IN der App
 * (laden + pruefen), sonst der bisherige Weg (System-Browser bzw. im Web
 * `/#/app`). `jetztInstallieren`: Phase 2 (sichern + installieren).
 *
 * Ergaenzungen zum Vertrag (die vereinbarten Felder bleiben unveraendert):
 * `installiert`, `installation`, `jetztInstallieren`, `abbrechen`,
 * `imBrowserLaden`, `erneutVersuchen` (haengender Bestaetigungsdialog),
 * `zuruecksetzen` (Leiste weggeklickt).
 */
export function useAppUpdate(): {
    status: UpdateStatus;
    release: AppRelease | null;
    installiert: InstallierteVersion | null;
    oeffneDownload: () => void;
    installation: InstallZustand;
    jetztInstallieren: () => void;
    abbrechen: () => void;
    imBrowserLaden: () => void;
    erneutVersuchen: () => void;
    zuruecksetzen: () => void;
} {
    const [befund, setBefund] = useState<UpdateBefund>(() => (pruefungAktiv() ? pruefer().letzterBefund() : LEERER_BEFUND));
    const updater = inAppUpdater();
    const installation = useSyncExternalStore(updater.abonniere, updater.zustand, updater.zustand);

    useEffect(() => {
        if (!pruefungAktiv()) return undefined;
        let aktiv = true;
        const pruefen = () => {
            void pruefer().pruefe().then((b) => {
                if (!aktiv) return;
                setBefund(b);
                if (b.release && b.status !== 'aktuell') void inAppUpdater().nachNeustart(b.release);
            });
        };
        const zurueck = () => {
            void inAppUpdater().beiRueckkehr();
            pruefen();
        };
        const beiSichtbarkeit = () => {
            if (document.visibilityState === 'visible') zurueck();
        };
        pruefen();
        document.addEventListener('visibilitychange', beiSichtbarkeit);
        document.addEventListener('resume', zurueck);
        return () => {
            aktiv = false;
            document.removeEventListener('visibilitychange', beiSichtbarkeit);
            document.removeEventListener('resume', zurueck);
        };
    }, []);

    const release = befund.release;
    const oeffneDownload = useCallback(() => {
        void inAppUpdater().starte(release);
    }, [release]);
    const jetztInstallieren = useCallback(() => {
        void inAppUpdater().jetztInstallieren();
    }, []);
    const erneutVersuchen = useCallback(() => {
        void inAppUpdater().jetztInstallieren({ erneut: true });
    }, []);
    const abbrechen = useCallback(() => {
        void inAppUpdater().abbrechen();
    }, []);
    const imBrowserLaden = useCallback(() => {
        void inAppUpdater().imBrowserLaden(release);
    }, [release]);
    const zuruecksetzen = useCallback(() => {
        inAppUpdater().zuruecksetzen();
    }, []);

    return {
        status: befund.status,
        release,
        installiert: befund.installiert,
        oeffneDownload,
        installation,
        jetztInstallieren,
        abbrechen,
        imBrowserLaden,
        erneutVersuchen,
        zuruecksetzen,
    };
}
